import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, FileClock, FileWarning, FileCheck, FileBadge, Download, Eye } from "lucide-react";
import { RH_TEMPLATES } from "@/lib/rhTemplates";

const ICONS = { FileText, FileClock, FileWarning, FileCheck, FileBadge };

export default function ChefTemplatesRH() {
  const [entreprise, setEntreprise] = useState(null);
  const [salaries, setSalaries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [salarieId, setSalarieId] = useState("");
  const [opts, setOpts] = useState({});
  const [open, setOpen] = useState(false);

  useEffect(() => { (async () => {
    try {
      const [me, s] = await Promise.all([api.get("/auth/me"), api.get("/chef/salaries")]);
      setEntreprise(me.data.entreprise || {});
      setSalaries(s.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  })(); }, []);

  const openTpl = (tpl) => {
    setSelected(tpl);
    setOpts({});
    setSalarieId(salaries[0]?.id || "");
    setOpen(true);
  };

  const buildAndDownload = (preview = false) => {
    if (!selected) return;
    if (selected.id !== "certificat" && !salarieId) {
      toast.error("Sélectionnez un salarié");
      return;
    }
    const sal = salaries.find((x) => x.id === salarieId) || {};
    const doc = selected.generator({ entreprise, salarie: sal, options: opts });
    if (preview) {
      const blob = doc.output("blob");
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } else {
      const filename = `${selected.id}-${(sal.code || "doc").replace(/[^\w-]/g, "")}-${new Date().toISOString().slice(0, 10)}.pdf`;
      doc.save(filename);
      toast.success("PDF généré");
    }
  };

  const FIELDS = {
    cdi: [
      { k: "date_debut", l: "Date d'embauche", type: "date" },
      { k: "taux_horaire", l: "Taux horaire (€)", type: "number" },
      { k: "duree_hebdo", l: "Durée hebdo (h)", type: "number", default: 35 },
      { k: "periode_essai", l: "Période d'essai (mois)", type: "number", default: 2 },
      { k: "lieu", l: "Lieu de travail" },
    ],
    cdd: [
      { k: "date_debut", l: "Date début", type: "date" },
      { k: "date_fin", l: "Date fin", type: "date" },
      { k: "taux_horaire", l: "Taux horaire (€)", type: "number" },
      { k: "motif", l: "Motif du CDD", type: "textarea", default: "accroissement temporaire d'activité" },
      { k: "lieu", l: "Lieu de travail" },
    ],
    licenciement: [
      { k: "motif", l: "Motif (cause)", default: "motif personnel" },
      { k: "detail", l: "Détail des faits", type: "textarea" },
      { k: "date_preavis", l: "Date entretien préalable", type: "date" },
      { k: "duree_preavis", l: "Durée préavis (mois)", type: "number", default: 2 },
    ],
    demission: [
      { k: "date_demission", l: "Date du courrier", type: "date" },
      { k: "duree_preavis", l: "Durée préavis (mois)", type: "number", default: 1 },
      { k: "date_fin", l: "Dernier jour", type: "date" },
    ],
    certificat: [
      { k: "date_entree", l: "Date d'entrée", type: "date" },
      { k: "date_sortie", l: "Date de sortie", type: "date" },
    ],
    solde: [
      { k: "date_sortie", l: "Date de fin de contrat", type: "date" },
      { k: "motif_sortie", l: "Motif de rupture", default: "fin de contrat" },
      { k: "salaire_brut", l: "Salaire brut du mois (€)", type: "number" },
      { k: "salaire_net", l: "Salaire net (€)", type: "number" },
      { k: "conges_payes", l: "Indemnité congés payés (€)", type: "number" },
      { k: "primes", l: "Primes / gratifications (€)", type: "number", default: 0 },
      { k: "total", l: "TOTAL net versé (€)", type: "number" },
    ],
    france_travail: [
      { k: "date_entree", l: "Date d'entrée", type: "date" },
      { k: "date_sortie", l: "Date de sortie", type: "date" },
      { k: "motif_sortie", l: "Motif de rupture", default: "fin de contrat" },
      { k: "derniere_remu", l: "Dernière rémunération brute mensuelle (€)", type: "number" },
      { k: "preavis_effectue", l: "Préavis effectué ?", default: "Oui" },
    ],
  };

  const fields = selected ? FIELDS[selected.id] || [] : [];

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Documents RH"
        title="Modèles RH prêts à l'emploi"
        sub="Générez en un clic les contrats, lettres de licenciement, accusés de démission et certificats de travail."
      />
      <div className="p-6 lg:p-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rh-templates-grid">
          {RH_TEMPLATES.map((tpl) => {
            const Ico = ICONS[tpl.icon] || FileText;
            return (
              <article key={tpl.id} className="border border-[#DEE2E6] bg-white p-5 hover:shadow-lg transition-shadow flex flex-col" data-testid={`tpl-${tpl.id}`}>
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 bg-[#002FA7] text-white flex items-center justify-center"><Ico className="h-5 w-5" /></div>
                  <div className="flex-1">
                    <h3 className="font-display text-lg font-black tracking-tight">{tpl.label}</h3>
                    <p className="text-xs text-[#495057] mt-1">{tpl.description}</p>
                  </div>
                </div>
                <Button onClick={() => openTpl(tpl)} data-testid={`tpl-open-${tpl.id}`} className="mt-4 h-10 bg-[#002FA7] text-white">
                  <FileText className="h-4 w-4 mr-2" /> Générer
                </Button>
              </article>
            );
          })}
        </div>

        <div className="mt-8 text-[10px] text-[#868E96] uppercase tracking-wider">
          Documents juridiques générés à titre indicatif. Faites-les valider par votre conseil ou la convention collective avant signature définitive.
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="rh-tpl-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight">{selected?.label}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3">
              {selected.id !== "certificat" || (() => true)() ? (
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Salarié destinataire</Label>
                  <Select value={salarieId} onValueChange={setSalarieId}>
                    <SelectTrigger className="h-11 mt-1" data-testid="tpl-salarie"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>{salaries.map((s) => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom} ({s.code})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ) : null}

              <div className="grid grid-cols-2 gap-3">
                {fields.map((f) => (
                  <div key={f.k} className={f.type === "textarea" ? "col-span-2" : ""}>
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold">{f.l}</Label>
                    {f.type === "textarea" ? (
                      <Textarea
                        value={opts[f.k] ?? f.default ?? ""}
                        onChange={(e) => setOpts({ ...opts, [f.k]: e.target.value })}
                        rows={2}
                        className="mt-1"
                        data-testid={`tpl-${f.k}`}
                      />
                    ) : (
                      <Input
                        type={f.type || "text"}
                        value={opts[f.k] ?? f.default ?? ""}
                        onChange={(e) => setOpts({ ...opts, [f.k]: e.target.value })}
                        className="h-11 mt-1"
                        data-testid={`tpl-${f.k}`}
                      />
                    )}
                  </div>
                ))}
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => buildAndDownload(true)} data-testid="tpl-preview-btn"><Eye className="h-4 w-4 mr-2" /> Aperçu</Button>
                <Button onClick={() => buildAndDownload(false)} className="bg-[#002FA7] text-white" data-testid="tpl-download-btn"><Download className="h-4 w-4 mr-2" /> Télécharger PDF</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
